import { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import MixerButtonIcon from '../assets/mixer.svg?react';
import { LocalStorageKey, usabilityTooltipDisplayCount } from '../data/client_local_storage.ts';
import { useLocalStorage } from '../data/use_local_storage.tsx';
import { isMobile } from '../util/billing.ts';
import { PdfHeaderHeight } from '../util/pdf_viewer.tsx';
import { SpeechBubble, SpeechBubbleLocation, SpeechBubbleOrientation } from '../util/speech_bubble.tsx';
import { MixerControls, mixerLandscapeContainerQuery, mixerPortraitContainerQuery } from './mixer_controls.tsx';
import { FadingButton, useButtonFade } from './player_util.tsx';

export function Mixer() {
  const [isMixerVisible, setIsMixerVisible] = useLocalStorage(LocalStorageKey.IsMixerVisible);

  const showPDF = useCallback(() => {
    setIsMixerVisible(false);
  }, [setIsMixerVisible]);

  const onStopPropagation = useCallback((event: any) => {
    event.stopPropagation(); // prevent mixer from closing
  }, []);

  return (
    <MixerWrapper $isMobile={isMobile()} $isMixerVisible={isMixerVisible} onClick={onStopPropagation}>
      <MixerControls showPDF={showPDF} />
    </MixerWrapper>
  );
}

export const playButtonMargin = '20px';
const maxMixerLongDimension = '850px';
const maxMixerShortDimensionPortrait = '350px';
const maxMixerShortDimensionLandscape = '350px';
const mixerButtonSize = 'calc(var(--play-button-size) * 0.7)';
const mixerMarginIncludingButton = `calc(var(--play-button-size) + 2 * ${playButtonMargin})`;
const hiddenLocation = `calc((${mixerButtonSize}) / 2 + ${playButtonMargin})`;

export const MixerBoundingBox = styled.div<{ $isMobile: boolean; $isMixerVisible: boolean }>`
  position: absolute;
  @container ${mixerPortraitContainerQuery} {
    --mixer-box-width: min(${maxMixerShortDimensionPortrait}, calc(100% - (2 * ${playButtonMargin})));
    --mixer-box-height: min(${maxMixerLongDimension}, calc(100% - ${PdfHeaderHeight}));
    bottom: 0;
    right: calc(50% - var(--mixer-box-width) / 2); /* Center horizontally */
  }
  @container ${mixerLandscapeContainerQuery} {
    --mixer-box-width: min(${maxMixerLongDimension}, calc(100% - ${playButtonMargin}));
    --mixer-box-height: min(
      ${maxMixerShortDimensionLandscape},
      calc(100% - (${playButtonMargin}) - ${PdfHeaderHeight})
    );
    right: 0;
    bottom: calc(50% - var(--mixer-box-height) / 2); /* Center vertically */
  }

  width: var(--mixer-box-width);
  height: var(--mixer-box-height);

  background-color: transparent;
  pointer-events: none;
`;

const MixerWrapper = styled.div<{ $isMobile: boolean; $isMixerVisible: boolean }>`
  position: absolute;
  @container ${mixerPortraitContainerQuery} {
    --mixer-width: 100%;
    --mixer-height: calc(100% - ${mixerMarginIncludingButton});
    ${(props) =>
      props.$isMixerVisible
        ? `bottom: ${mixerMarginIncludingButton}; right: 0;`
        : `bottom: ${hiddenLocation}; right: calc(${mixerButtonSize} / 2);`};
  }
  @container ${mixerLandscapeContainerQuery} {
    --mixer-width: calc(100% - ${mixerMarginIncludingButton});
    --mixer-height: 100%;
    ${(props) =>
      props.$isMixerVisible
        ? `right: ${mixerMarginIncludingButton}; bottom: 0;`
        : `right: ${hiddenLocation}; bottom: calc(${mixerButtonSize} / 2);`};
  }
  border-radius: 5%;
  ${(props) =>
    props.$isMixerVisible
      ? `
        width: var(--mixer-width);
        max-height: var(--mixer-height);
        box-shadow: 0 0 20px 5px var(--color-pdf-text);
        padding: min(5vh, 40px) min(5vw, 40px);`
      : `
        width: 0;
        max-height: 0;
      `}

  transition:
    width 250ms ease-out,
    height 250ms ease-out,
    max-width 250ms ease-out,
    max-height 250ms ease-out,
    bottom 250ms ease-out,
    right 250ms ease-out,
    top 250ms ease-out,
    padding 250ms ease-out,
    box-shadow 250ms ease-out;

  background-color: var(--color-background);
  overflow: hidden;
  pointer-events: all;
`;

export function MixerButton() {
  const [isMixerVisible, setIsMixerVisible] = useLocalStorage(LocalStorageKey.IsMixerVisible);
  const fade = useButtonFade();
  const [isPlayerMaximized] = useLocalStorage(LocalStorageKey.IsPlayerMaximized);

  const onClick = useCallback(
    (event: any) => {
      setIsMixerVisible((isMixerVisible) => !isMixerVisible);
      event.stopPropagation(); // prevent mixer from closing
    },
    [setIsMixerVisible],
  );

  const [tooltipCount, setTooltipCount] = useLocalStorage(LocalStorageKey.TooltipVisibilityCount);
  useEffect(() => {
    if (!isPlayerMaximized) {
      return;
    }
    setTooltipCount((tooltipCount) =>
      (tooltipCount.mixerButton ?? 0) < usabilityTooltipDisplayCount + 1
        ? { ...tooltipCount, mixerButton: (tooltipCount.mixerButton ?? 0) + 1 }
        : tooltipCount,
    );
  }, [isPlayerMaximized, setTooltipCount]);

  const isTooltipVisible = isPlayerMaximized && (tooltipCount.mixerButton ?? 0) <= usabilityTooltipDisplayCount;

  // Tap here to close
  return (
    <MixerButtonWrapper id={mixerButtonId} onClick={onClick} $fade={fade && !isTooltipVisible}>
      {isTooltipVisible && (
        <SpeechBubble
          $orientation={SpeechBubbleOrientation.Vertical}
          $location={SpeechBubbleLocation.Before}
          $horizontalOffset={'-5ch'}
        >
          <MixerButtonTooltip>Tap to {isMixerVisible ? 'close' : 'open'} mixer</MixerButtonTooltip>
        </SpeechBubble>
      )}
      <MixerButtonIconWrapper onClick={() => {}} />
    </MixerButtonWrapper>
  );
}

export const mixerButtonId = 'mixer-button';

const MixerButtonWrapper = styled(FadingButton)<{ $fade: boolean }>`
  position: absolute;
  @container ${mixerPortraitContainerQuery} {
    right: 0;
    /* Center vertically */
    top: 50%;
    transform: translateY(-50%);
  }
  @container ${mixerLandscapeContainerQuery} {
    bottom: 0;
    /* Center horizontally */
    right: 50%;
    transform: translateX(50%);
  }
  width: ${mixerButtonSize};
  height: ${mixerButtonSize};
  border-radius: 50%;
  cursor: pointer;
  /* while faded over the sheet music, drop the gold for the pdf colors so the button reads as
     transparent gray (issue #3389); hover un-fades (see FadingButton), so it restores the colors */
  fill: ${(props) => (props.$fade ? 'var(--color-pdf-background)' : 'var(--color-mixer-button-icon)')};
  background: ${(props) => (props.$fade ? 'var(--color-pdf-text)' : 'var(--color-mixer-button-background)')};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      fill: var(--color-mixer-button-icon);
      background: var(--color-mixer-button-background);
    }
  }
  pointer-events: all;
`;

const MixerButtonIconWrapper = styled(MixerButtonIcon)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateY(-50%) translateX(-50%);
  width: 60%;
  height: 60%;
`;

const MixerButtonTooltip = styled.div`
  width: 12ch;
  font-family: Jost, sans-serif;
  padding: 0.5rem 1rem;
  text-align: center;
  line-height: 1;
`;
